import React from 'react'
import { View } from '@components/atoms'

import {
	useAppSelector,
	useAppDispatch,
	paymentActions,
	CartType,
	PaymentCostType,
	PaymentDeliveryType,
	CheckoutQuoteType,
	ApiGetDataStatus,
} from '@redux/index'
import { formatAmount } from '@utils'
import { useDisplayCurrency } from '@components/systems/monolith/useDisplayCurrency'
import { produce } from 'immer'

/**
 * 결제 화면의 Total 블록 — Monolith(2안).
 *
 * 2026-09-16 부터 **계산하지 않는다.** 서버 견적(`state.payment.quote`, judahsWeb/CHECKOUT_PLAN.md §2)이
 * 준 소계·쿠폰·배송비·합계를 통화 그대로 찍는다. 원화(KR)면 원화 단계 값(phaseKrw)으로 온 견적이라
 * 화면·청구·인보이스가 같은 숫자다. 옛 구현의 VAT 10/110 역산·원화 소계 reduce 는 지웠다.
 *
 * 쿠폰 적용 = 코드가 실린 견적을 다시 받는 것. 유효하지 않으면 견적이 문장을 돌려준다.
 */
export const MonolithPaymentTotalCost = () => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// redux state
	const cartList: CartType[] = useAppSelector(state => state.payment.tryToPayment)[
		'cartList'
	]
	const cost: PaymentCostType = useAppSelector(state => state.payment.paymentCost)
	const quote: CheckoutQuoteType | null = useAppSelector(state => state.payment.quote)
	const quoteStatus: ApiGetDataStatus = useAppSelector(state => state.payment.quoteStatus)
	const errorMessage: string = useAppSelector(
		state => state.payment.paymentCouponErrorMessage,
	)

	/*
		값 잠금이 얼마나 남았는가. 잠금은 결제 화면을 연 순간 시작하고 USD 15분 · KRW 30분이다
		(PAYMENT.md §3). 끝나는 **시각**(`21:47`)을 적으면 읽는 쪽이 뺄셈을 해야 한다 — 남은
		시간을 분·초로 센다. 잠금이 있을 때만 타이머가 돈다.
	*/
	const lockExpiresAt: string = quote !== null ? quote.lockExpiresAt : ''
	const [now, setNow] = React.useState<number>(() => Date.now())
	React.useEffect(() => {
		if (lockExpiresAt === '') return undefined
		const timer = window.setInterval(() => setNow(Date.now()), 1000)
		return () => window.clearInterval(timer)
	}, [lockExpiresAt])

	/** 단계 판매(얼리버드·프리오더) 줄이 섞여 있는가 */
	const hasPreorderLine: boolean =
		quote !== null &&
		quote.lines.some(aLine => ['earlybird', 'preorder'].includes(aLine.phase))

	const lockLine: string = React.useMemo(() => {
		if (lockExpiresAt === '') return ''
		const diff = new Date(lockExpiresAt).getTime() - now
		if (diff <= 0) return 'We no longer hold these prices.'
		const sec = Math.floor(diff / 1000)
		const m = Math.floor(sec / 60)
		const sc = sec % 60
		return `We hold these prices for ${m}:${String(sc).padStart(2, '0')}.`
	}, [lockExpiresAt, now])
	const delivery: PaymentDeliveryType = useAppSelector(
		state => state.payment.paymentDelivery,
	)

	/*
		Shop·Cart 가 보여 준 표시 통화. 결제 통화(배송국)와 다르면 — 원화로 보다가 해외 배송지를 고른
		한국 방문자, 또는 그 반대 — 왜 바뀌었는지 한 줄 적는다 (PRICING §6, 2026-09-16). 사실만.
	*/
	const displayCurrency = useDisplayCurrency()
	/*
		통화는 견적이 정하고, 견적 전에는 배송국이 대신한다. 배송국이 아직 비어 있으면 표시 통화를
		쓴다 — 원화만 보던 사람에게 "Total (USD)" 가 한 프레임이라도 서면 안 된다. 사가가 곧 KR 을
		채우고(`presetKoreanDeliverySaga`) 견적이 KRW 로 온다.
	*/
	const currency =
		quote?.currency ??
		(delivery.countryIsoCode === 'KR' ||
		(delivery.countryIsoCode === '' && displayCurrency === 'KRW')
			? 'KRW'
			: 'USD')
	const money = (value: number): string => formatAmount(currency, value)
	const totalQty = cartList.reduce((sumQty, aCart) => sumQty + aCart.qty, 0)
	const isLoading = quote === null || quoteStatus === 'loading'

	const onCouponChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const str = e.target.value

		const newCost = produce(cost, draft => {
			draft.couponCode = str
		})

		reduxDispatch(paymentActions.PAYMENT_COST({ ...newCost }))
		reduxDispatch(paymentActions.PAYMENT_COUPON_ERROR_MESSAGE(''))
	}

	const onCouponApplyClick = () => {
		if (cost.couponCode !== '') {
			reduxDispatch(paymentActions.TRIGGER_COUPON_APPLY(true))
		}
	}

	const couponApplied: boolean =
		quote?.coupon.applied === true && quote.discountAmount > 0

	return (
		<View className="mnPaySection">
			<View className="mnPaySectionHead">
				<span className="mnKicker">Total</span>
				{quote !== null && quote.lines.some(aLine => aLine.phase !== '') && (
					<p className="mnPaySectionNote">
						{quote.lines
							.filter(aLine => aLine.phase !== '')
							.map(
								aLine =>
									`${aLine.productName}: ${
										aLine.phase === 'earlybird'
											? 'Early bird'
											: aLine.phase === 'preorder'
											? 'Pre-order'
											: 'List'
									} price`,
							)
							.join(' · ')}
					</p>
				)}
			</View>

			<View className="mnPaySectionBody">
				<View className="mnPayCostRow">
					<span className="mnPayCostLabel">{`Subtotal · ${totalQty} items`}</span>
					<span className="mnPayCostValue">
						{isLoading ? '—' : money(quote.subtotalAmount)}
					</span>
				</View>

				<View className="mnPayCostRow">
					<span className="mnPayCostLabel">Shipping</span>
					<span className="mnPayCostValue">
						{isLoading ? '—' : money(quote.shippingAmount)}
					</span>
				</View>

				{/*
					관세는 목적지가 어디든 회사가 낸다(DDP). 금액이 아니라 사실 하나만 적는다 — 보이지 않던
					비용을 보이는 가치로 바꾸는 행이고, 견적과 무관한 표시 전용이다 (PRICING §8, 2026-09-17).
					국내 배송(KR)은 관세가 없으니 행을 그리지 않는다.
				*/}
				{delivery.countryIsoCode !== 'KR' && (
					<View className="mnPayCostRow">
						<span className="mnPayCostLabel">Import duties</span>
						<span className="mnPayCostValue">Included</span>
					</View>
				)}

				<View className="mnField">
					<label className="mnLabel" htmlFor="mnPayCoupon">
						Coupon code
					</label>
					<View className="mnPayCoupon">
						<View className="mnField mnPayCouponField">
							<input
								id="mnPayCoupon"
								type="text"
								className={`mnInput ${errorMessage !== '' ? 'error' : ''}`}
								value={cost.couponCode}
								onChange={onCouponChange}
							/>
						</View>
						<button
							type="button"
							className="mnCtaGhost mnPayCouponApply"
							disabled={cost.couponCode === '' || isLoading}
							onClick={onCouponApplyClick}
						>
							Apply
						</button>
					</View>
					{errorMessage !== '' && <span className="mnError">{errorMessage}</span>}
				</View>

				{couponApplied && quote !== null && (
					<View className="mnPayCostRow">
						<span className="mnPayCostLabel">{`Coupon · ${quote.coupon.code}`}</span>
						<span className="mnPayCostValue">{`- ${money(quote.discountAmount)}`}</span>
					</View>
				)}

				<View className="mnPayCostRow total">
					<span className="mnPayCostLabel">{`Total (${currency})`}</span>
					<span className="mnPayCostValue">
						{isLoading ? '—' : money(quote.paidAmount)}
					</span>
				</View>

				{!isLoading && displayCurrency !== currency && (
					<p className="mnPaySectionNote">
						{currency === 'KRW'
							? 'Orders shipped to Korea are charged in KRW, VAT included.'
							: 'Orders shipped outside Korea are charged in USD.'}
					</p>
				)}

				{/*
					배송국을 고른 뒤에만, 그 나라의 결과 하나만 (PRICING §8.3). 미국은 국경에서 걷는 소비세가
					없어 문 앞에서 낼 것이 없고, 그 밖은 자국 VAT·GST 를 캐리어가 징수한다. 나라별 비교표는
					어디에도 두지 않는다.
				*/}
				{delivery.countryIsoCode !== '' && delivery.countryIsoCode !== 'KR' && (
					<p className="mnPaySectionNote">
						{delivery.countryIsoCode === 'US'
							? 'There is nothing to pay on delivery.'
							: 'Your country collects its VAT or GST on delivery.'}
					</p>
				)}

				{hasPreorderLine && (
					/* 스토어 정책과 같은 문장 (`storePolicy_en.md` Pre-Order Policy) */
					<p className="mnPaySectionNote">
						{
							'You can cancel a pre-order any time before it ships. We refund the full amount.'
						}
					</p>
				)}

				{lockLine !== '' && <p className="mnPaySectionNote">{lockLine}</p>}
			</View>
		</View>
	)
}
